import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { LocaleDatePipe } from '@app/pipes/locale-date.pipe';
import { TimePipe } from '@app/pipes/time.pipe';
import { ScheduleModel } from '@app/reducers/orm/schedule/schedule.model';
import { DashboardFilterType } from '@app/reducers/page-filters/page-filters-dashboard.config';
import { selectAllowedDashboardFilter } from '@app/reducers/page-filters/page-filters-dashboard.selectors';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { selectAuthUserId } from '@reducers/auth/auth.selectors';
import { AppState } from '@reducers/interfaces';
import { getSchedulesForAuthenticatedUserWithinPeriod, ScheduleService } from '@reducers/orm/schedule/schedule.service';
import { PageFiltersService } from '@reducers/page-filters/page-filters.service';
import { mgcIconCalendarTimeAddCuteRe } from '@sb/mgc-icons/mgcIcon-calendar_time_add_cute_re.icon';
import { DashboardWidgetSelectComponent } from '@sb/ui/dashboard-widget/dashboard-widget-select.component';
import { DashboardWidgetComponent } from '@sb/ui/dashboard-widget/dashboard.widget.component';
import { IconRegistry } from '@sb/ui/icon';
import { toISODateString } from '@shiftbase-com/ts-shared/utilities';
import { addWeeks } from 'date-fns';
import { finalize } from 'rxjs';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'my-schedule-widget',
  imports: [
    DashboardWidgetComponent,
    DashboardWidgetSelectComponent,
    LocaleDatePipe,
    TimePipe,
    RouterLink,
    TranslatePipe,
  ],
  templateUrl: './my-schedule-widget.component.html',
})
export class MyScheduleWidgetComponent implements OnInit {
  private store = inject<Store<AppState>>(Store);
  private scheduleService = inject(ScheduleService);
  private pageFilterService = inject(PageFiltersService);
  private iconRegistry = inject(IconRegistry);
  private translate = inject(TranslateService);

  periodOptions = [
    { label: this.translate.instant('week'), value: 1 },
    { label: this.translate.instant('2 weeks'), value: 2 },
    { label: this.translate.instant('3 weeks'), value: 3 },
    { label: this.translate.instant('4 weeks'), value: 4 },
  ];

  isLoading = signal(false);
  schedules = computed<ScheduleModel[]>(() =>
    this.store.selectSignal(
      getSchedulesForAuthenticatedUserWithinPeriod(
        toISODateString(new Date()),
        toISODateString(addWeeks(new Date(), this.widgetPeriod())),
      ),
    )(),
  );

  private filterType = DashboardFilterType.MY_SCHEDULE;
  private dashboardFilter = this.store.selectSignal(selectAllowedDashboardFilter(this.filterType));
  widgetPeriod = computed(() => this.dashboardFilter()?.widgetPeriod ?? 1);
  private userId = this.store.selectSignal(selectAuthUserId);

  constructor() {
    this.iconRegistry.registerIcons([mgcIconCalendarTimeAddCuteRe]);
  }

  ngOnInit() {
    this.isLoading.set(true);
    const requestData = {
      userId: this.userId(),
      minDate: toISODateString(new Date()),
      maxDate: toISODateString(addWeeks(new Date(), 4)),
    };
    void this.scheduleService
      .load(requestData)
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe();
  }

  onPeriodChange(period: number | string) {
    this.pageFilterService.setFilter('dashboard', this.filterType, {
      widgetPeriod: period,
    });
  }
}
